// src/common/components/cart-modal/CartModalContent.tsx
import React, { useMemo, useState } from "react";
import { useTheme } from "../../hooks/useTheme";
import {
  CartModalStage,
  DeliveryService,
  DeliveryType,
  PaymentMethod,
} from "../../types/enums";
import { useCartModal } from "./hooks/useCartModal";
import { MdArrowBack } from "react-icons/md";
import styles from "./CartModalContent.module.scss";
import CartStage from "./components/cartStage/CartStage";
import DeliveryTypeSelector from "./components/deliveryTypeSelector/DeliveryTypeSelector";
import StoreSelector from "./components/storeSelector/StoreSelector";
import Button from "../button/Button";
import AddressCard from "./components/addressCard/AddressCard";
import BonusInfo from "./components/bonusInfo/BonusInfo";
import PromoSelector from "./components/promoSelector/PromoSelector";
import BonusSpendSelector from "./components/bonusSpendSelector/BonusSpendSelector";
import BonusEarnInfo from "./components/bonusEarnInfo/BonusEarnInfo";
import BonusMotivator from "./components/bonusMotivator/BonusMotivator";
import PaymentMethodSelector from "./components/paymentMethodSelector/PaymentMethodSelector";
import ValidationError from "./components/validationError/ValidationError";
import OrderSummary from "./components/orderSummary/OrderSummary";
import OrderSuccessStage from "./components/orderSuccessStage/OrderSuccessStage";
import { DeliveryTimeSelector } from "./components/deliveryTimeSelector/DeliveryTimeSelector";
import AddressForm from "../addressForm/AddressForm";
import { PaymentMethodsContent } from "../../../pages/paymentMetod/сomponents/paymentMethodsContent/PaymentMethodsContent";
import { YooKassaPaymentStage } from "./components/yooKassaPaymentStage/YooKassaPaymentStage";
import { ProcessingStage } from "./components/processingStage/ProcessingStage";
import { ROUTES } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import OrderDetailsSection from "./components/orderDetailsSection/OrderDetailsSection";
import { useSelector } from "react-redux";
import {
  selectClientUserId,
  selectIsExecutor,
} from "../../../store/slices/executorSlice";
import { PaymasterPaymentStage } from "./components/paymasterPaymentStage/PaymasterPaymentStage";
import { selectCurrentCluster } from "../../../store/slices/currentClusterSlice";
import GiftSelector from "./components/giftSelector/GiftSelector";
import CdekSelector from "./components/cdekSelector/CdekSelector";
import CutlerySelector from "./components/cutlerySelector/CutlerySelector";

interface CartModalContentProps {
  onClose: () => void;
}

export default function CartModalContent({ onClose }: CartModalContentProps) {
  const { theme } = useTheme();
  const currentCluster = useSelector(selectCurrentCluster);

  const {
    cart,
    stage,
    deliveryType,
    selectedAddressId,
    paymentMethod,
    orderNumber,
    addresses,
    orderDisabled,
    validationError,
    itemsTotal,
    deliveryFee,
    discountAmount,
    totalWithDelivery,
    selectedPromo,
    discountCode,
    applicablePromos,
    bonusBalance,
    bonusPercent,
    bonusesToSpend,
    earnedBonuses,
    nextLevelText,
    maxWriteOff,
    storeList,
    handlePickUpStoreChange,
    pickUpStore,
    getSaleItemName,
    getAddressList,
    setStage,
    createOrder,
    startOrder,
    handleDeliveryTypeChange,
    handleAddressSelect,
    handlePaymentMethodChange,
    handleSelectPromo,
    setDiscountCode,
    validatePromoBeforeOrder,
    setBonusesToSpend,
    getBestDeliveryRuleText,
    bonusMode,
    handleBonusModeChange,
    itemsForOrder,
    itemsRemaining,
    currentBestRule,
    nextBestRule,
    bestPossibleRule,
    deliveryAvailable,
    deliveryError,
    desiredTime,
    deliveryTimeDisplay,
    deliveryTimePickerVisible,
    openDeliveryTimePicker,
    closeDeliveryTimePicker,
    onDeliveryTimeSelect,
    deliveryTimeMinimumDate,
    savedCards,
    selectedCardId,
    handleCardSelect,
    isYookassaEnabled,
    paymentToken,
    paymentId,
    phone,
    comment,
    getValidationResult,
    inputHandler,
    setDirty,
    MAX_COMMENT_LENGTH,
    isOrderCreating,
    paymentUrl,
    paymentFailed,
    paymentError,
    isTimeAvailable,
    selectedGiftItemId,
    setSelectedGiftItemId,
    cdekOptions,
    cdekLoading,
    cdekError,
    selectedTariff,
    selectedPickupPoint,
    setSelectedTariff,
    setSelectedPickupPoint,
    setDeliveryService,
    setCdekTariffCode,
    deliveryService,
    cutleryCount,
    incrementCutlery,
    decrementCutlery,
    applicableRules,
  } = useCartModal();

  const navigate = useNavigate();

  const [isAddingAddress, setIsAddingAddress] = useState(false);

  const isDeliveryTypeNotSelected = deliveryType === DeliveryType.UNSET;
  const isStoreNotSelected =
    deliveryType === DeliveryType.PICKUP && !pickUpStore;
  const isAddressNotSelected =
    deliveryType === DeliveryType.DELIVERY && !selectedAddressId;
  const isPaymentMethodNotSelected = paymentMethod === PaymentMethod.UNSET;
  const isPromoNotValid = selectedPromo && !validatePromoBeforeOrder();
  const isExecutor = useSelector(selectIsExecutor);
  const clientUserId = useSelector(selectClientUserId);
  const isAnonymousMode = isExecutor && !clientUserId;

  const canSpendBonuses = bonusBalance > 0 && maxWriteOff > 0;

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      {stage === CartModalStage.CART && (
        <CartStage
          onClose={onClose}
          onCheckout={startOrder}
          getSaleItemName={getSaleItemName}
          cutleryCount={cutleryCount}
          incrementCutlery={incrementCutlery}
          decrementCutlery={decrementCutlery}
        />
      )}

      {stage === CartModalStage.CHECKOUT && (
        <div className={styles.container}>
          <div
            className={styles.header}
            style={{ borderBottomColor: theme.border }}
          >
            <button
              className={styles.backButton}
              onClick={() => setStage(CartModalStage.CART)}
            >
              <MdArrowBack size={24} color={theme.text} />
            </button>
            <div className={styles.titleContainer}>
              <h2 className={styles.title} style={{ color: theme.text }}>
                Оформление заказа
              </h2>
              <p
                className={styles.subtitle}
                style={{ color: theme.textSecondary }}
              >
                Способ получения
              </p>
            </div>
          </div>

          <div className={styles.content}>
            {/* Способ получения */}
            <div
              className={`${styles.section} ${isDeliveryTypeNotSelected ? styles.required : ""}`}
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              {isDeliveryTypeNotSelected && (
                <p
                  className={styles.requiredText}
                  style={{ color: theme.danger }}
                >
                  Выберите способ получения
                </p>
              )}
              <DeliveryTypeSelector
                deliveryType={deliveryType}
                onSelect={handleDeliveryTypeChange}
              />
              {/* Доставка СДЭК */}
            </div>

            {/* Магазин для самовывоза */}
            {deliveryType === DeliveryType.PICKUP && storeList.length > 0 && (
              <div
                className={`${styles.section} ${isStoreNotSelected ? styles.required : ""}`}
                style={{
                  backgroundColor: theme.card,
                  borderColor: theme.border,
                }}
              >
                {isStoreNotSelected && (
                  <p
                    className={styles.requiredText}
                    style={{ color: theme.danger }}
                  >
                    Выберите магазин для самовывоза
                  </p>
                )}
                <StoreSelector
                  handlePickUpStoreChange={handlePickUpStoreChange}
                  pickUpStore={pickUpStore}
                />
              </div>
            )}

            {/* Адрес доставки */}
            {deliveryType === DeliveryType.DELIVERY && (
              <div
                className={`${styles.section} ${isAddressNotSelected ? styles.required : ""}`}
                style={{
                  backgroundColor: theme.card,
                  borderColor: theme.border,
                }}
              >
                <h3
                  className={styles.sectionTitle}
                  style={{ color: theme.text }}
                >
                  Адрес доставки
                </h3>

                {isAddressNotSelected && (
                  <p
                    className={styles.requiredText}
                    style={{ color: theme.danger }}
                  >
                    Выберите адрес доставки
                  </p>
                )}

                <div className={styles.addressesList}>
                  {isAddingAddress && (
                    <AddressForm
                      onClose={() => setIsAddingAddress(false)}
                      getList={getAddressList}
                    />
                  )}
                  {!isAddingAddress && (
                    <Button
                      title="Добавить новый адрес"
                      onClick={() => setIsAddingAddress(true)}
                      variant="secondary"
                      fullWidth
                    />
                  )}
                  {addresses.map((address) => (
                    <AddressCard
                      key={address.id}
                      address={address}
                      isSelected={selectedAddressId === address.id}
                      onSelect={handleAddressSelect}
                    />
                  ))}
                </div>
              </div>
            )}

            {cdekOptions && (
              <div
                className={styles.section}
                style={{
                  backgroundColor: theme.card,
                  borderColor: theme.border,
                }}
              >
                <CdekSelector
                  tariffs={cdekOptions.tariffs}
                  pickupPoints={cdekOptions.pickupPoints}
                  selectedTariff={selectedTariff}
                  selectedPickupPoint={selectedPickupPoint}
                  onSelectTariff={(tariff) => {
                    setSelectedTariff(tariff);
                    setDeliveryService(DeliveryService.CDEK);
                    setCdekTariffCode(tariff.tariff_code);
                  }}
                  onSelectPickupPoint={setSelectedPickupPoint}
                />
              </div>
            )}

            <div
              className={styles.section}
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              <OrderDetailsSection
                phone={phone}
                comment={comment}
                getValidationResult={getValidationResult}
                inputHandler={inputHandler}
                setDirty={setDirty}
                maxCommentLength={MAX_COMMENT_LENGTH}
              />
            </div>

            {/* Промо-акции */}
            {!isAnonymousMode &&
              applicablePromos &&
              applicablePromos.length > 0 && (
                <div
                  className={`${styles.section} ${isPromoNotValid ? styles.required : ""}`}
                  style={{
                    backgroundColor: theme.card,
                    borderColor: theme.border,
                  }}
                >
                  <h3
                    className={styles.sectionTitle}
                    style={{ color: theme.text }}
                  >
                    Промо-акции
                  </h3>

                  {isPromoNotValid && (
                    <p
                      className={styles.requiredText}
                      style={{ color: theme.danger }}
                    >
                      Выбранная промо-акция недействительна
                    </p>
                  )}

                  <PromoSelector
                    applicablePromos={applicablePromos}
                    selectedPromo={selectedPromo}
                    onSelectPromo={handleSelectPromo}
                    discountCode={discountCode}
                    onDiscountCodeChange={setDiscountCode}
                    cartItems={itemsForOrder}
                    storeId={pickUpStore}
                    deliveryType={deliveryType}
                  />

                  {selectedPromo?.giftBySum && (
                    <GiftSelector
                      giftItemIds={selectedPromo.giftItemIds || []}
                      selectedGiftId={selectedGiftItemId}
                      onSelect={setSelectedGiftItemId}
                    />
                  )}
                </div>
              )}

            {/* Бонусы */}
            {!isAnonymousMode && currentCluster?.withBonuses && (
              <div
                className={styles.section}
                style={{
                  backgroundColor: theme.card,
                  borderColor: theme.border,
                }}
              >
                <BonusInfo balance={bonusBalance} percent={bonusPercent} />

                <div
                  className={styles.modeSelector}
                  style={{ borderColor: theme.border }}
                >
                  <button
                    className={`${styles.modeButton} ${bonusMode === "earn" ? styles.active : ""}`}
                    onClick={() => handleBonusModeChange("earn")}
                  >
                    <span
                      className={bonusMode === "earn" ? styles.activeText : ""}
                    >
                      Копить
                    </span>
                  </button>
                  <button
                    className={`${styles.modeButton} ${bonusMode === "spend" ? styles.active : ""} ${!canSpendBonuses ? styles.disabled : ""}`}
                    onClick={() => {
                      if (canSpendBonuses) {
                        handleBonusModeChange("spend");
                      }
                    }}
                    disabled={!canSpendBonuses}
                  >
                    <span
                      className={bonusMode === "spend" ? styles.activeText : ""}
                    >
                      Тратить
                    </span>
                  </button>
                </div>

                <div className={styles.bonusSection}>
                  {bonusMode === "spend" && canSpendBonuses && (
                    <BonusSpendSelector
                      balance={bonusBalance}
                      maxWriteOff={maxWriteOff}
                      value={bonusesToSpend}
                      onChange={setBonusesToSpend}
                    />
                  )}

                  {bonusMode === "earn" && (
                    <>
                      <div
                        className={styles.earnInfo}
                        style={{ backgroundColor: `${theme.primary}` }}
                      >
                        <p
                          className={styles.earnText}
                          style={{ color: "#fff" }}
                        >
                          После выполнения заказа начислится {earnedBonuses}{" "}
                          бонусов
                        </p>
                      </div>
                      <BonusEarnInfo
                        earned={earnedBonuses}
                        percent={bonusPercent}
                      />
                    </>
                  )}

                  <BonusMotivator text={nextLevelText} />
                </div>
              </div>
            )}

            {/* Время получения */}
            {deliveryService === DeliveryService.CUSTOM && (
              <div
                className={styles.section}
                style={{
                  backgroundColor: theme.card,
                  borderColor: theme.border,
                }}
              >
                <DeliveryTimeSelector
                  displayText={deliveryTimeDisplay}
                  pickerVisible={deliveryTimePickerVisible}
                  minimumDate={deliveryTimeMinimumDate}
                  onPress={openDeliveryTimePicker}
                  onClose={closeDeliveryTimePicker}
                  onSelect={onDeliveryTimeSelect}
                  isTimeAvailable={isTimeAvailable}
                />
              </div>
            )}

            {/* Способ оплаты */}
            <div
              className={`${styles.section} ${isPaymentMethodNotSelected ? styles.required : ""}`}
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              {isPaymentMethodNotSelected && (
                <p
                  className={styles.requiredText}
                  style={{ color: theme.danger }}
                >
                  Выберите способ оплаты
                </p>
              )}
              <PaymentMethodSelector
                paymentMethod={paymentMethod}
                onSelect={handlePaymentMethodChange}
                deliveryType={deliveryType}
              />
            </div>

            {paymentMethod === PaymentMethod.ONLINE &&
              isYookassaEnabled &&
              savedCards.length > 0 && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Сохранённые карты</h3>
                  <PaymentMethodsContent
                    mode="modal"
                    onSelect={handleCardSelect}
                    selectedId={selectedCardId ?? undefined}
                  />

                  <Button
                    title="Новая карта"
                    onClick={() => handleCardSelect(null)}
                    variant="outline"
                    size="small"
                    fullWidth
                    style={{ marginTop: 12 }}
                  />
                </div>
              )}

            {validationError && (
              <div
                className={styles.section}
                style={{
                  backgroundColor: theme.card,
                  borderColor: theme.border,
                }}
              >
                <ValidationError message={validationError} />
              </div>
            )}

            {/* Сумма заказа */}
            <div
              className={styles.section}
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              <OrderSummary
                orderItems={itemsForOrder}
                remainingItems={itemsRemaining}
                subtotal={itemsTotal}
                deliveryFee={deliveryFee}
                discountAmount={discountAmount}
                bonusesSpent={bonusMode === "spend" ? bonusesToSpend : 0}
                bonusesToAdd={bonusMode === "earn" ? earnedBonuses : 0}
                total={totalWithDelivery}
                getSaleItemName={getSaleItemName}
                hasPromo={!!selectedPromo}
                deliveryType={deliveryType}
                bestDeliveryText={getBestDeliveryRuleText(itemsTotal, applicableRules)}
                storeName={storeList.find((s) => s.id === pickUpStore)?.name}
                currentBestRule={currentBestRule}
                nextBestRule={nextBestRule}
                bestPossibleRule={bestPossibleRule}
                deliveryAvailable={deliveryAvailable}
                deliveryError={deliveryError}
                selectedAddressId={selectedAddressId}
                selectedGiftItemId={selectedGiftItemId}
                deliveryService={deliveryService}
                cdekOptions={cdekOptions}
                cutleryCount={
                  currentCluster?.cutlery ? cutleryCount : undefined
                }
              />
            </div>

            <div
              className={styles.section}
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              <Button
                title="Заказать"
                onClick={createOrder}
                variant="primary"
                disabled={orderDisabled}
                loading={isOrderCreating}
                fullWidth
              />
            </div>
          </div>
        </div>
      )}

      {stage === CartModalStage.ORDER_SUCCESS && (
        <OrderSuccessStage
          onClose={onClose}
          orderNumber={orderNumber}
          paymentFailed={paymentFailed}
          paymentError={paymentError}
        />
      )}

      {stage === CartModalStage.YOOKASSA_PAYMENT &&
        paymentToken &&
        paymentId && (
          <YooKassaPaymentStage
            token={paymentToken}
            paymentId={paymentId}
            onSuccess={() => setStage(CartModalStage.ORDER_SUCCESS)}
            onError={() => setStage(CartModalStage.CHECKOUT)}
            onBack={() => setStage(CartModalStage.CHECKOUT)}
          />
        )}

      {stage === CartModalStage.PAYMASTER_PAYMENT && paymentUrl && (
        <PaymasterPaymentStage
          paymentUrl={paymentUrl}
          onSuccess={() => setStage(CartModalStage.ORDER_SUCCESS)}
          onError={() => setStage(CartModalStage.CHECKOUT)}
          onBack={() => setStage(CartModalStage.CHECKOUT)}
        />
      )}

      {stage === CartModalStage.PROCESSING && orderNumber && (
        <ProcessingStage
          orderId={orderNumber}
          onPaid={() => setStage(CartModalStage.ORDER_SUCCESS)}
          onTimeout={() => {}}
          onGoToOrders={() => navigate(ROUTES.ORDERS)}
          onContinueShopping={onClose}
        />
      )}
    </div>
  );
}
